import { useMemo, useState } from "react";
import { Topbar, AddPropertyButton } from "../components/Topbar";
import { StatusBadge } from "../components/StatusBadge";
import { AddPropertyModal } from "../components/AddPropertyModal";
import { useAuth } from "../context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { LayoutGrid, Rows3, MapPin, ChevronRight } from "lucide-react";
import {
  PROPERTIES,
  OWNERS,
  getOwner,
  PROP_STATUS_TONE,
  STATUS_LABELS,
} from "../data/mockData";
import { useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";

export default function Properties() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [view, setView] = useState("cards");
  const [country, setCountry] = useState("All");
  const [status, setStatus] = useState("All");
  const [owner, setOwner] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);

  const filtered = useMemo(
    () =>
      PROPERTIES.filter((p) => {
        if (country !== "All" && p.country !== country) return false;
        if (status !== "All" && p.complianceStatus !== status) return false;
        if (owner !== "All" && p.ownerId !== owner) return false;
        return true;
      }),
    [country, status, owner]
  );

  return (
    <>
      <Topbar
        title="Properties"
        subtitle="Portfolio detail — six residences across Mallorca and Ibiza"
        action={isAdmin ? <AddPropertyButton onClick={() => setModalOpen(true)} /> : null}
      />

      <div className="px-6 lg:px-8 py-8 space-y-6">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="grid grid-cols-3 gap-2">
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger data-testid="prop-filter-country" className="h-9 text-xs w-full md:w-36">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All regions</SelectItem>
                <SelectItem value="Mallorca">Mallorca</SelectItem>
                <SelectItem value="Ibiza">Ibiza</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="prop-filter-status" className="h-9 text-xs w-full md:w-52">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All statuses</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="expiring">Renewal approaching</SelectItem>
                <SelectItem value="action">Action required</SelectItem>
                <SelectItem value="incomplete">Information incomplete</SelectItem>
              </SelectContent>
            </Select>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger data-testid="prop-filter-owner" className="h-9 text-xs w-full md:w-48">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All owners</SelectItem>
                {OWNERS.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div
            role="tablist"
            aria-label="View mode"
            className="inline-flex items-center rounded-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1"
          >
            <button
              role="tab"
              aria-selected={view === "cards"}
              data-testid="view-toggle-cards"
              onClick={() => setView("cards")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-colors",
                view === "cards"
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              )}
            >
              <LayoutGrid size={13} /> Cards
            </button>
            <button
              role="tab"
              aria-selected={view === "table"}
              data-testid="view-toggle-table"
              onClick={() => setView("table")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-colors",
                view === "table"
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              )}
            >
              <Rows3 size={13} /> Table
            </button>
          </div>
        </div>

        {view === "cards" ? (
          <section
            data-testid="properties-cards"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
          >
            {filtered.length === 0 && (
              <div className="col-span-full rounded-md border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-sm text-slate-500">
                No properties match the current filters.
              </div>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                data-testid={`property-card-${p.id}`}
                onClick={() => navigate(`/properties/${p.id}`)}
                className="text-left group rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 transition-colors overflow-hidden"
              >
                {/* Header strip */}
                <div className="bg-slate-900 dark:bg-slate-950 relative h-28 flex items-end p-4">
                  <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.10]"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.7) 1px, transparent 1px)",
                      backgroundSize: "24px 24px",
                    }}
                  />
                  <div className="relative">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
                      {p.reference}
                    </p>
                    <h3 className="font-display text-xl text-white font-semibold tracking-tight">
                      {p.name}
                    </h3>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <MapPin size={12} /> {p.city}, {p.country}
                  </div>
                  <StatusBadge tone={PROP_STATUS_TONE[p.complianceStatus]}>
                    {STATUS_LABELS[p.complianceStatus]}
                  </StatusBadge>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400">
                        Current
                      </p>
                      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 tabular-nums">
                        {p.requirementCounts.current}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400">
                        Expiring
                      </p>
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400 tabular-nums">
                        {p.requirementCounts.expiring}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400">
                        Missing
                      </p>
                      <p className="text-sm font-medium text-rose-700 dark:text-rose-400 tabular-nums">
                        {p.requirementCounts.missing}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-1 text-xs text-slate-500 dark:text-slate-400">
                    <span>{getOwner(p.ownerId)?.name}</span>
                    <span className="inline-flex items-center gap-1 text-slate-800 dark:text-slate-200 group-hover:translate-x-0.5 transition-transform">
                      View <ChevronRight size={12} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </section>
        ) : (
          <section
            data-testid="properties-table"
            className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/40 text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                    <th className="py-3 px-6 text-left">Property</th>
                    <th className="py-3 px-4 text-left">Owner</th>
                    <th className="py-3 px-4 text-left">Location</th>
                    <th className="py-3 px-4 text-left">Status</th>
                    <th className="py-3 px-4 text-left">Capacity</th>
                    <th className="py-3 px-4 text-left">Next expiry</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`property-row-${p.id}`}
                      onClick={() => navigate(`/properties/${p.id}`)}
                      className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-6">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{p.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                          {p.reference}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                        {getOwner(p.ownerId)?.name}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                        {p.city} · {p.country}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge tone={PROP_STATUS_TONE[p.complianceStatus]}>
                          {STATUS_LABELS[p.complianceStatus]}
                        </StatusBadge>
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {p.bedrooms} bd · {p.capacity} pax
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {p.nextExpiryDate || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-400">
                        <ChevronRight size={16} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      <AddPropertyModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
